import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useChat } from "@/context/ChatContext";

const LINKS = [
  { href: "#como-funciona", label: "Como funciona" },
  { href: "#comparativo", label: "Comparativo" },
  { href: "#imoveis", label: "Imóveis" },
  { href: "#veiculos", label: "Veículos" },
  { href: "#sobre", label: "Sobre" },
  { href: "#faq", label: "Dúvidas" },
];

export default function Navbar() {
  const { waDefault, openChat } = useChat();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.documentElement.style.overflow = menuOpen ? "hidden" : "";
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${scrolled ? "glass py-2" : "bg-transparent py-4"}`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-6">
        <a href="#topo" data-testid="nav-logo-link" className="flex items-center gap-3">
          <img src="/favicon.svg" alt="Logotipo Isabela Guedes" className="h-10 w-10 rounded-xl" />
          <span className="leading-tight">
            <span className="block font-display text-lg font-bold text-slate-900">Isabela Guedes</span>
            <span className="block text-[11px] font-bold uppercase tracking-[0.18em] text-brand-600">
              Consultora Porto Seguro
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navegação principal">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm font-semibold text-slate-600 transition-colors hover:text-brand-600"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-chat-button"
            onClick={() => openChat(null)}
            className="hidden rounded-full bg-brand-600 px-5 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-700 hover:shadow-lg sm:block"
          >
            Simular agora
          </button>
          <a
            href={waDefault()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-button"
            className="hidden rounded-full border border-brand-600/20 bg-white px-5 py-2.5 text-sm font-bold text-brand-600 transition-all hover:-translate-y-0.5 hover:shadow-lg sm:block"
          >
            WhatsApp
          </a>
          <button
            data-testid="nav-mobile-toggle"
            aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-full p-2.5 text-slate-700 transition-colors hover:bg-slate-100 lg:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] bg-white lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex items-center justify-between px-5 py-4">
            <img src="/favicon.svg" alt="Logotipo Isabela Guedes" className="h-10 w-10 rounded-xl" />
            <button
              data-testid="nav-mobile-close"
              aria-label="Fechar menu"
              onClick={() => setMenuOpen(false)}
              className="rounded-full p-3 text-slate-700 transition-colors hover:bg-slate-100"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex flex-col px-6" aria-label="Menu mobile">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
                className="border-b border-slate-100 py-5 font-display text-2xl font-bold text-slate-900 transition-colors hover:text-brand-600"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="space-y-3 px-6 pt-8">
            <button
              data-testid="nav-mobile-chat-button"
              onClick={() => { setMenuOpen(false); openChat(null); }}
              className="h-14 w-full rounded-full bg-brand-600 text-base font-bold text-white"
            >
              Simular agora
            </button>
            <a
              href={waDefault()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-whatsapp-button"
              className="flex h-14 w-full items-center justify-center rounded-full border-2 border-brand-600/15 text-base font-bold text-brand-600"
            >
              WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
